import React, { useState } from 'react';
import { useProject, useProjects, useUpdateProjectSettings } from '../api/projects';
import {
  useAddProjectMember,
  useAdminUsers,
  useCreateUser,
  useDeactivateUser,
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProjectMember,
  useUpdateUser,
  type ProjectMemberRole,
} from '../api/admin';
import { useMe } from '../api/auth';
import type { UserRole } from '../lib/types';

export default function Admin() {
  const [tab, setTab] = useState<'users' | 'access'>('users');
  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-xl font-bold mb-3">Admin</h1>
      <div className="border-b border-slate-200 flex gap-1 mb-4">
        <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
          Users
        </TabButton>
        <TabButton active={tab === 'access'} onClick={() => setTab('access')}>
          Project Access
        </TabButton>
      </div>
      {tab === 'users' ? <UsersTab /> : <ProjectAccessTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm -mb-px border-b-2 ${
        active
          ? 'border-emerald-600 text-emerald-700 font-medium'
          : 'border-transparent text-slate-600 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Users tab ───────────────────────────────────────────────────────

function UsersTab() {
  const users = useAdminUsers();
  const me = useMe();
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-600">
          {users.data?.length ?? 0} user{users.data?.length === 1 ? '' : 's'}. The system
          pseudo-user is hidden.
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"
        >
          + New user
        </button>
      </div>

      {users.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {users.data && users.data.length === 0 && (
        <div className="text-sm text-slate-500">No users yet — create one above.</div>
      )}
      {users.data && users.data.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium w-px whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((u) => (
                <UserRow key={u.id} user={u} isSelf={me.data?.id === u.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function UserRow({
  user,
  isSelf,
}: {
  user: { id: string; name: string; email: string; role: UserRole; active: boolean };
  isSelf: boolean;
}) {
  const updateUser = useUpdateUser();
  const deactivate = useDeactivateUser();
  const [editing, setEditing] = useState(false);

  const actionError =
    (deactivate.error || updateUser.error)
      ? ((deactivate.error || updateUser.error) as Error).message
      : null;

  return (
    <>
      <tr className="border-b border-slate-100 last:border-0">
        <td className="px-3 py-2">
          {user.name}
          {isSelf && <span className="ml-1 text-xs text-slate-400">(you)</span>}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-slate-700">{user.email}</td>
        <td className="px-3 py-2">
          <RoleBadge role={user.role} />
        </td>
        <td className="px-3 py-2">
          {user.active ? (
            <span className="text-emerald-700 text-xs">● active</span>
          ) : (
            <span className="text-slate-400 text-xs">○ deactivated</span>
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-slate-600 hover:text-emerald-700 underline"
            >
              Edit
            </button>
            {user.active && !isSelf && (
              <button
                type="button"
                disabled={deactivate.isPending}
                onClick={() => {
                  if (window.confirm(`Deactivate ${user.name}? Their sessions will be revoked.`)) {
                    deactivate.mutate(user.id);
                  }
                }}
                className="text-xs text-red-700 hover:text-red-800 underline disabled:opacity-50"
              >
                {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
              </button>
            )}
            {!user.active && (
              <button
                type="button"
                disabled={updateUser.isPending}
                onClick={() => updateUser.mutate({ id: user.id, active: true })}
                className="text-xs text-emerald-700 hover:text-emerald-800 underline disabled:opacity-50"
              >
                {updateUser.isPending ? 'Reactivating…' : 'Reactivate'}
              </button>
            )}
          </div>
          {actionError && (
            <div className="text-[10px] text-red-700 mt-1">{actionError}</div>
          )}
        </td>
      </tr>
      {editing && (
        <EditUserModal
          user={user}
          isSelf={isSelf}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const create = useCreateUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [password, setPassword] = useState('');

  return (
    <Modal onClose={onClose} title="New user">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            { email: email.trim(), name: name.trim(), role, password },
            { onSuccess: onClose },
          );
        }}
        className="space-y-3"
      >
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            autoFocus
          />
        </Field>
        <Field label="Name">
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className="input">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
        <Field label="Password (min 8 chars)">
          <input
            type="text"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="Tell the user this password out-of-band; they can change it later"
          />
        </Field>
        {create.error && <div className="text-sm text-red-700">{(create.error as Error).message}</div>}
        <ModalActions
          onCancel={onClose}
          submitLabel={create.isPending ? 'Creating…' : 'Create user'}
          submitDisabled={create.isPending || !email || !name || password.length < 8}
        />
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  isSelf,
  onClose,
}: {
  user: { id: string; name: string; email: string; role: UserRole };
  isSelf: boolean;
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<UserRole>(user.role);
  const [password, setPassword] = useState('');

  return (
    <Modal onClose={onClose} title={`Edit ${user.email}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const patch: Parameters<typeof update.mutate>[0] = { id: user.id };
          if (name !== user.name) patch.name = name.trim();
          if (role !== user.role) patch.role = role;
          if (password) patch.password = password;
          update.mutate(patch, { onSuccess: onClose });
        }}
        className="space-y-3"
      >
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="input"
            disabled={isSelf}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          {isSelf && (
            <p className="text-xs text-slate-500 mt-1">
              You can&apos;t change your own role. Ask another admin.
            </p>
          )}
        </Field>
        <Field label="Reset password (leave blank to keep current)">
          <input
            type="text"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="New password (min 8 chars)"
          />
        </Field>
        {update.error && <div className="text-sm text-red-700">{(update.error as Error).message}</div>}
        <ModalActions
          onCancel={onClose}
          submitLabel={update.isPending ? 'Saving…' : 'Save changes'}
          submitDisabled={update.isPending}
        />
      </form>
    </Modal>
  );
}

// ─── Project Access tab ──────────────────────────────────────────────

function ProjectAccessTab() {
  const projects = useProjects();
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(undefined);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-4">
      <div className="bg-white border border-slate-200 rounded p-2">
        <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide px-2 py-1">
          Projects
        </h3>
        {projects.data?.length === 0 && (
          <div className="px-2 py-1 text-sm text-slate-500">
            No projects yet. Create one from the dashboard.
          </div>
        )}
        <ul className="mt-1">
          {projects.data?.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelectedSlug(p.slug)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                  selectedSlug === p.slug ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-slate-50'
                }`}
              >
                <div className="font-medium">{p.name}</div>
                <div className="font-mono text-xs text-slate-500">{p.slug}</div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        {selectedSlug ? (
          <div className="space-y-6">
            <ProjectSettingsPanel slug={selectedSlug} />
            <MembersPanel slug={selectedSlug} />
          </div>
        ) : (
          <div className="text-sm text-slate-500">Select a project on the left.</div>
        )}
      </div>
    </div>
  );
}

/**
 * Per-project settings for the parallel-planning architecture (Phase 8).
 * Lets admins tune `planning_base_branch` (which branch the planning
 * worktree pool tracks) and `planning_concurrency` (how many planning
 * workers run per project).
 *
 * Changes to concurrency need a panel-worker restart to take effect on
 * already-initialized pools; the API returns a `note` we surface here.
 */
function ProjectSettingsPanel({ slug }: { slug: string }) {
  const project = useProject(slug);
  const update = useUpdateProjectSettings(slug);
  const [baseBranch, setBaseBranch] = useState('');
  const [concurrency, setConcurrency] = useState<number>(3);
  const [pipelineVersion, setPipelineVersion] = useState<'v1' | 'v2'>('v1');
  const [executionMode, setExecutionMode] = useState<'standard' | 'phase-agentic'>('standard');
  const [commitStrategy, setCommitStrategy] = useState<'per_step' | 'squash'>('squash');
  const [autoPush, setAutoPush] = useState<boolean>(false);
  const [pushRemote, setPushRemote] = useState<string>('origin');
  const [fullSetup, setFullSetup] = useState<boolean>(true);
  const [browserTesting, setBrowserTesting] = useState<boolean>(false);
  const [note, setNote] = useState<string | null>(null);

  // Re-seed local state when the loaded project changes (project switch
  // or after a successful save).
  React.useEffect(() => {
    if (project.data) {
      setBaseBranch(project.data.planning_base_branch ?? 'main');
      setConcurrency(project.data.planning_concurrency ?? 3);
      setPipelineVersion(project.data.pipeline_version === 'v2' ? 'v2' : 'v1');
      setExecutionMode(project.data.execution_mode === 'phase-agentic' ? 'phase-agentic' : 'standard');
      setCommitStrategy(project.data.commit_strategy === 'per_step' ? 'per_step' : 'squash');
      setAutoPush(project.data.auto_push === true);
      setPushRemote(project.data.push_remote ?? 'origin');
      setFullSetup(project.data.full_setup !== false);
      setBrowserTesting(project.data.browser_testing === true);
    }
  }, [project.data]);

  if (!project.data) {
    return <div className="text-sm text-slate-500">Loading project…</div>;
  }
  const initial = project.data;
  const dirty =
    baseBranch.trim() !== (initial.planning_base_branch ?? '') ||
    concurrency !== (initial.planning_concurrency ?? 3) ||
    pipelineVersion !== ((initial.pipeline_version === 'v2' ? 'v2' : 'v1')) ||
    executionMode !== (initial.execution_mode === 'phase-agentic' ? 'phase-agentic' : 'standard') ||
    commitStrategy !== (initial.commit_strategy === 'per_step' ? 'per_step' : 'squash') ||
    autoPush !== (initial.auto_push === true) ||
    pushRemote.trim() !== (initial.push_remote ?? 'origin') ||
    fullSetup !== (initial.full_setup !== false) ||
    browserTesting !== (initial.browser_testing === true);

  return (
    <div>
      <h3 className="text-sm font-medium mb-2">Planning pool settings — {slug}</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setNote(null);
          update.mutate(
            {
              planning_base_branch: baseBranch.trim(),
              planning_concurrency: concurrency,
              pipeline_version: pipelineVersion,
              execution_mode: executionMode,
              commit_strategy: commitStrategy,
              auto_push: autoPush,
              push_remote: pushRemote.trim() || 'origin',
              full_setup: fullSetup,
              browser_testing: browserTesting,
            },
            {
              onSuccess: (res) => setNote(res.note ?? null),
            },
          );
        }}
        className="bg-slate-50 border border-slate-200 rounded p-3 space-y-3"
      >
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">
            Planning base branch
          </label>
          <input
            type="text"
            value={baseBranch}
            onChange={(e) => setBaseBranch(e.target.value)}
            placeholder="main"
            className="input w-full max-w-xs"
          />
          <div className="text-xs text-slate-500 mt-1">
            Each planning worktree is pinned to this branch and reset to it
            before every plan run.
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">
            Planning concurrency (1–10)
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={concurrency}
            onChange={(e) => setConcurrency(Number(e.target.value))}
            className="input w-24"
          />
          <div className="text-xs text-slate-500 mt-1">
            Maximum simultaneous planning tasks per project. Each one runs
            in its own git worktree. The execution pool stays at 1 to keep
            commits serialized.
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">
            Pipeline version
          </label>
          <select
            value={pipelineVersion}
            onChange={(e) => setPipelineVersion(e.target.value === 'v2' ? 'v2' : 'v1')}
            className="input w-40"
          >
            <option value="v1">v1 (stable)</option>
            <option value="v2">v2 (beta)</option>
          </select>
          <div className="text-xs text-slate-500 mt-1">
            Which pipeline runs this project's tasks. v2 is the typed
            plan-as-code rewrite — same approve/answer flow. Needs
            CLAUDE_PIPELINE_V2_BIN set in the worker env.
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">
            Execution mode (v2)
          </label>
          <select
            value={executionMode}
            onChange={(e) =>
              setExecutionMode(e.target.value === 'phase-agentic' ? 'phase-agentic' : 'standard')
            }
            className="input w-56"
            disabled={pipelineVersion !== 'v2'}
          >
            <option value="standard">standard — plan all steps, run per-step</option>
            <option value="phase-agentic">phase-agentic — lazy phases, run + verify per phase</option>
          </select>
          <div className="text-xs text-slate-500 mt-1">
            v2 only. “phase-agentic” plans a phase skeleton, then plans + implements
            each phase just-in-time in one resumed session, verifying after every
            phase. A per-task config_override.execution_mode overrides this default.
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">
            Git — commit strategy (v2)
          </label>
          <select
            value={commitStrategy}
            onChange={(e) => setCommitStrategy(e.target.value === 'per_step' ? 'per_step' : 'squash')}
            className="input w-56"
          >
            <option value="squash">squash — one commit per task</option>
            <option value="per_step">per step — one commit each</option>
          </select>
          <div className="text-xs text-slate-500 mt-1">
            How v2 shapes branch history. Per-step commits always run during
            execution for crash-resume; “squash” collapses them into one
            clean commit at the end.
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoPush}
              onChange={(e) => setAutoPush(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600"
            />
            <span className="text-xs text-slate-700">
              Auto-push the feature branch on success (v2)
            </span>
          </label>
          <div className="text-xs text-slate-500 mt-1 ml-6">
            Pushes the pipeline’s own branch to the remote when a task
            completes. Never force-pushes; refuses main/master.
          </div>
          {autoPush && (
            <input
              type="text"
              value={pushRemote}
              onChange={(e) => setPushRemote(e.target.value)}
              placeholder="origin"
              className="input w-40 mt-2 ml-6"
            />
          )}
        </div>
        <div className="border-t border-slate-200 pt-3">
          <div className="text-xs font-medium text-slate-600 mb-1">
            Project capabilities (v2)
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={fullSetup}
              onChange={(e) => setFullSetup(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600"
            />
            <span className="text-xs text-slate-700">
              Fully set up — can build / test (run the verifier)
            </span>
          </label>
          <div className="text-xs text-slate-500 mt-1 ml-6">
            Off for a freshly-cloned, un-installed project: the pipeline skips
            build/test/lint and only edits → reviews → commits → pushes.
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none mt-2">
            <input
              type="checkbox"
              checked={browserTesting}
              onChange={(e) => setBrowserTesting(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600"
            />
            <span className="text-xs text-slate-700">
              Browser testing — can run a headless browser smoke check
            </span>
          </label>
          <div className="text-xs text-slate-500 mt-1 ml-6">
            Requires “fully set up”. (Takes effect once the browser smoke check
            ships; the toggle is saved now.)
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={!dirty || update.isPending}
            className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save settings'}
          </button>
          {update.isError && (
            <span className="text-xs text-red-600">
              {(update.error as Error).message}
            </span>
          )}
          {note && (
            <span className="text-xs text-amber-700">{note}</span>
          )}
        </div>
      </form>
    </div>
  );
}

function MembersPanel({ slug }: { slug: string }) {
  const members = useProjectMembers(slug);
  const add = useAddProjectMember(slug);
  const update = useUpdateProjectMember(slug);
  const remove = useRemoveProjectMember(slug);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectMemberRole>('contributor');

  return (
    <div>
      <h3 className="text-sm font-medium mb-2">Members of {slug}</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate(
            { email: email.trim(), role },
            { onSuccess: () => setEmail('') },
          );
        }}
        className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded p-2"
      >
        <div className="flex-1 min-w-[14rem]">
          <label className="block text-xs text-slate-600 mb-0.5">User email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alice@example.com"
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-0.5">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as ProjectMemberRole)}
            className="input"
          >
            <option value="contributor">Contributor</option>
            <option value="admin">Project Admin</option>
            <option value="owner">Owner</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={!email || add.isPending}
          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
      {add.error && <div className="text-sm text-red-700 mb-2">{(add.error as Error).message}</div>}

      {members.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {members.data && members.data.length === 0 && (
        <div className="text-sm text-slate-500">
          No members yet — add one above.
        </div>
      )}
      {members.data && members.data.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium w-px whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <tr key={m.user_id} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2">{m.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{m.email}</td>
                  <td className="px-3 py-2">
                    <select
                      value={m.role}
                      onChange={(e) =>
                        update.mutate({
                          userId: m.user_id,
                          role: e.target.value as ProjectMemberRole,
                        })
                      }
                      className="border border-slate-300 rounded px-1 py-0.5 text-xs"
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Project Admin</option>
                      <option value="contributor">Contributor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Remove ${m.name} from ${slug}?`)) {
                          remove.mutate(m.user_id);
                        }
                      }}
                      className="text-xs text-red-700 hover:text-red-800 underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Shared UI atoms ─────────────────────────────────────────────────

function RoleBadge({ role }: { role: UserRole }) {
  const map: Record<UserRole, string> = {
    admin: 'bg-amber-100 text-amber-800 border-amber-300',
    member: 'bg-slate-100 text-slate-700 border-slate-300',
    system: 'bg-slate-100 text-slate-500 border-slate-300',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase ${map[role]}`}>
      {role}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ModalActions({
  onCancel,
  submitLabel,
  submitDisabled,
}: {
  onCancel: () => void;
  submitLabel: string;
  submitDisabled: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitDisabled}
        className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        {submitLabel}
      </button>
    </div>
  );
}

function Modal({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-3">{title}</h2>
        {children}
      </div>
    </div>
  );
}
